import React, { useCallback, useEffect, useRef, useState } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { useTranslation } from 'react-i18next';
import { getAktifDil } from '../i18n/i18n';

interface TodoItem {
    todoID: number;
    kullaniciID: number;
    baslik: string;
    renk: string;
    tamamlandi: boolean;
    siraNo: number;
    olusturmaTarihi: string;
}

// ── Takvim tipleri ──────────────────────────────────────
interface CalNote {
    notID: number;
    kullaniciID: number;
    notTarihi: string;
    notBaslik?: string;
    notIcerik?: string;
    notRengi: string;
    aktifMi: boolean;
}

const CAL_COLOR_OPTIONS = [
    { value: 'primary',   label: 'Mavi',      bg: '#dbeafe' },
    { value: 'success',   label: 'Yeşil',     bg: '#d1fae5' },
    { value: 'danger',    label: 'Kırmızı',   bg: '#fde8e8' },
    { value: 'warning',   label: 'Sarı',      bg: '#fef3c7' },
    { value: 'info',      label: 'Açık Mavi', bg: '#e0f2fe' },
    { value: 'secondary', label: 'Gri',       bg: '#f3f4f6' },
];
function calColorToBg(name: string): string {
    return CAL_COLOR_OPTIONS.find(c => c.value === name)?.bg ?? '#dbeafe';
}
function toDateStr(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function shortDate(dateStr: string): string {
    const d = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00');
    return new Intl.DateTimeFormat(getAktifDil() || 'tr', { day: 'numeric', month: 'long' }).format(d);
}
function formatDate(d: Date): string {
    return new Intl.DateTimeFormat(getAktifDil() || 'tr', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(d);
}
// ────────────────────────────────────────────────────────

interface Props {
    open: boolean;
    onClose: () => void;
    kullaniciID: number;
    onCountChange?: (count: number) => void;
    onTodayCount?: (count: number) => void;
}

const PASTEL_COLORS = [
    { value: '#fde8e8', label: 'Kırmızı' },
    { value: '#fef3c7', label: 'Sarı' },
    { value: '#d1fae5', label: 'Yeşil' },
    { value: '#dbeafe', label: 'Mavi' },
    { value: '#ede9fe', label: 'Mor' },
    { value: '#f8e4d0', label: 'Turuncu' },
];

// Speech-to-text browser API type
interface SpeechRecognitionEvent extends Event {
    results: SpeechRecognitionResultList;
}
interface SpeechRecognitionErrorEvent extends Event {
    error: string;
}
declare global {
    interface Window {
        SpeechRecognition: any;
        webkitSpeechRecognition: any;
    }
}

const TodoPanel: React.FC<Props> = ({ open, onClose, kullaniciID, onCountChange, onTodayCount }) => {
    const { t } = useTranslation();
    const [todos, setTodos] = useState<TodoItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [newText, setNewText] = useState('');
    const [newColor, setNewColor] = useState(PASTEL_COLORS[5].value);
    const [listening, setListening] = useState(false);
    const [filter, setFilter] = useState<'all' | 'active' | 'done'>('active');
    const recognitionRef = useRef<any>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    // Sürükleme
    const dragIndexRef = useRef<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    // Yeni görev tarih seçimi
    const [newDateEnabled, setNewDateEnabled] = useState(false);
    const [newDateStr, setNewDateStr] = useState(toDateStr(new Date()));

    // ── Takvim state ──────────────────────────────────────
    const calToday = new Date(); calToday.setHours(0, 0, 0, 0);
    const todayStr = toDateStr(calToday);
    const [selectedDate, setSelectedDate] = useState<Date>(new Date(calToday));
    const [calMonth, setCalMonth] = useState<Date>(new Date(calToday.getFullYear(), calToday.getMonth(), 1));
    const [monthNotes, setMonthNotes] = useState<CalNote[]>([]);
    const [calLoading, setCalLoading] = useState(false);
    const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
    const [calFormBaslik, setCalFormBaslik] = useState('');
    const [calFormIcerik, setCalFormIcerik] = useState('');
    const [calFormRenk, setCalFormRenk] = useState(CAL_COLOR_OPTIONS[0].value);
    const datePickerRef = useRef<HTMLInputElement>(null);

    const fetchMonthNotes = useCallback(async () => {
        if (!kullaniciID) return;
        setCalLoading(true);
        try {
            const year = calMonth.getFullYear();
            const month = calMonth.getMonth();
            const firstDay = toDateStr(new Date(year, month, 1));
            const lastDay = toDateStr(new Date(year, month + 1, 0));
            const res = await axiosInstance.get(
                `/api/KullaniciNotlari/kullanici/${kullaniciID}?baslangicTarihi=${firstDay}&bitisTarihi=${lastDay}`
            );
            setMonthNotes(res.data);
        } catch { }
        finally { setCalLoading(false); }
    }, [kullaniciID, calMonth]);

    useEffect(() => { if (kullaniciID) fetchMonthNotes(); }, [fetchMonthNotes]);
    useEffect(() => { if (open) fetchMonthNotes(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // calMonth'u selectedDate ile senkronize et
    useEffect(() => {
        const newMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
        setCalMonth(prev => prev.getTime() === newMonth.getTime() ? prev : newMonth);
    }, [selectedDate]);

    const calTodayStr = toDateStr(calToday);
    useEffect(() => {
        const count = monthNotes.filter(n => n.notTarihi.slice(0, 10) === calTodayStr).length;
        onTodayCount?.(count);
    }, [monthNotes, onTodayCount, calTodayStr]);

    const selectedStr = toDateStr(selectedDate);
    const isToday = selectedStr === todayStr;
    const visibleCalNotes = monthNotes.filter(n => n.notTarihi.slice(0, 10) === selectedStr);

    const cancelEdit = () => {
        setEditingNoteId(null); setCalFormBaslik('');
        setCalFormIcerik(''); setCalFormRenk(CAL_COLOR_OPTIONS[0].value);
    };
    const startEditNote = (note: CalNote) => {
        setEditingNoteId(note.notID);
        setCalFormBaslik(note.notBaslik || '');
        setCalFormIcerik(note.notIcerik || '');
        setCalFormRenk(note.notRengi || CAL_COLOR_OPTIONS[0].value);
    };
    const handleCalUpdate = async (noteID: number, notTarihi: string) => {
        if (!calFormBaslik.trim()) return;
        try {
            await axiosInstance.put(`/api/KullaniciNotlari/${noteID}`, {
                notID: noteID, kullaniciID, notTarihi,
                notBaslik: calFormBaslik.trim(), notIcerik: calFormIcerik || null,
                notRengi: calFormRenk, aktifMi: true,
            });
            setMonthNotes(prev => prev.map(n =>
                n.notID === noteID
                    ? { ...n, notBaslik: calFormBaslik.trim(), notIcerik: calFormIcerik || undefined, notRengi: calFormRenk }
                    : n
            ));
            cancelEdit();
        } catch { }
    };
    const handleCalDelete = async (id: number) => {
        try {
            await axiosInstance.delete(`/api/KullaniciNotlari/${id}`);
            setMonthNotes(prev => prev.filter(n => n.notID !== id));
            if (editingNoteId === id) cancelEdit();
        } catch { }
    };

    const fetchTodos = useCallback(async () => {
        if (!kullaniciID) return;
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/api/TodoGorevler?kullaniciID=${kullaniciID}`);
            setTodos(res.data);
        } catch {
            // ignore
        } finally {
            setLoading(false);
        }
    }, [kullaniciID]);

    // Sayfa yüklendiğinde hemen fetch et (badge için)
    useEffect(() => {
        if (kullaniciID) fetchTodos();
    }, [fetchTodos]);

    // Panel açılınca taze veri çek
    useEffect(() => {
        if (open) fetchTodos();
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // Speech-to-text
    const startListening = () => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) { alert(t('msg.islemHatasi')); return; }
        const rec = new SR();
        rec.lang = 'tr-TR';
        rec.interimResults = false;
        rec.maxAlternatives = 1;
        rec.onstart = () => setListening(true);
        rec.onend = () => setListening(false);
        rec.onerror = (e: SpeechRecognitionErrorEvent) => { setListening(false); };
        rec.onresult = (e: SpeechRecognitionEvent) => {
            const transcript = e.results[0][0].transcript;
            setNewText(prev => (prev ? prev + ' ' + transcript : transcript));
        };
        recognitionRef.current = rec;
        rec.start();
    };

    const stopListening = () => {
        recognitionRef.current?.stop();
        setListening(false);
    };

    const handleAdd = async () => {
        const text = newText.trim();
        if (!text) return;
        if (newDateEnabled && newDateStr) {
            // Takvime not olarak ekle
            try {
                const res = await axiosInstance.post('/api/KullaniciNotlari', {
                    kullaniciID, notTarihi: newDateStr,
                    notBaslik: text, notRengi: 'primary', aktifMi: true,
                });
                const noteDate = new Date(newDateStr + 'T00:00:00');
                if (noteDate.getMonth() === calMonth.getMonth() && noteDate.getFullYear() === calMonth.getFullYear()) {
                    setMonthNotes(prev => [...prev, res.data]);
                }
                setSelectedDate(noteDate);
                setNewText('');
                setNewDateEnabled(false);
            } catch { }
        } else {
            // Todo listesine ekle
            try {
                const res = await axiosInstance.post('/api/TodoGorevler', {
                    kullaniciID,
                    baslik: text,
                    renk: newColor,
                    tamamlandi: false,
                    siraNo: 0,
                });
                setTodos(prev => [res.data, ...prev]);
                setNewText('');
            } catch { }
        }
    };

    const handleToggle = async (todo: TodoItem) => {
        try {
            await axiosInstance.patch(`/api/TodoGorevler/${todo.todoID}/tamamla`, !todo.tamamlandi, {
                headers: { 'Content-Type': 'application/json' }
            });
            setTodos(prev => prev.map(item => item.todoID === todo.todoID ? { ...t, tamamlandi: !item.tamamlandi } : t));
        } catch { }
    };

    const handleDelete = async (id: number) => {
        try {
            await axiosInstance.delete(`/api/TodoGorevler/${id}`);
            setTodos(prev => prev.filter(item => item.todoID !== id));
        } catch { }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAdd(); }
    };

    const filtered = todos.filter(item => {
        if (filter === 'active') return !item.tamamlandi;
        if (filter === 'done') return item.tamamlandi;
        return true;
    });

    const activeCount = todos.filter(item => !item.tamamlandi).length;

    // Aktif sayıyı üste bildir
    useEffect(() => {
        onCountChange?.(activeCount);
    }, [activeCount, onCountChange]);
    const handleDragStart = (index: number) => {
        dragIndexRef.current = index;
    };
    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        setDragOverIndex(index);
    };
    const handleDrop = async (index: number) => {
        const fromIndex = dragIndexRef.current;
        if (fromIndex === null || fromIndex === index) {
            dragIndexRef.current = null;
            setDragOverIndex(null);
            return;
        }
        // filtered içindeki gerçek todos index'lerini bul
        const fromTodo = filtered[fromIndex];
        const toTodo = filtered[index];
        const fromTotalIndex = todos.findIndex(t => t.todoID === fromTodo.todoID);
        const toTotalIndex = todos.findIndex(t => t.todoID === toTodo.todoID);
        // Yeni sırayı hesapla
        const newTodos = [...todos];
        const [moved] = newTodos.splice(fromTotalIndex, 1);
        newTodos.splice(toTotalIndex, 0, moved);
        // SiraNo güncelle
        const updated = newTodos.map((item, i) => ({ ...t, siraNo: i }));
        setTodos(updated);
        dragIndexRef.current = null;
        setDragOverIndex(null);
        // Backend'e kaydet (sıra değişen görevler)
        const changed = updated.filter((item, i) => t.siraNo !== todos[i]?.siraNo || t.todoID !== todos[i]?.todoID);
        await Promise.all(changed.map(item =>
            axiosInstance.put(`/api/TodoGorevler/${t.todoID}`, {
                kullaniciID: t.kullaniciID, baslik: t.baslik, renk: t.renk, tamamlandi: t.tamamlandi, siraNo: t.siraNo,
            }).catch(() => {})
        ));
    };
    const handleDragEnd = () => {
        dragIndexRef.current = null;
        setDragOverIndex(null);
    };

    return (
        <>
            {/* Overlay */}
            <div
                style={{
                    position: 'fixed', inset: 0, zIndex: 1049,
                    pointerEvents: open ? 'auto' : 'none',
                    opacity: open ? 1 : 0,
                    transition: 'opacity 0.25s',
                    background: 'rgba(10,20,40,0.18)',
                }}
                onClick={onClose}
            />

            {/* Panel */}
            <div style={{
                position: 'fixed', top: 0, right: 0,
                height: '100vh', width: 370,
                background: '#fff', zIndex: 1050,
                boxShadow: '-6px 0 32px rgba(26,36,64,0.13)',
                transform: open ? 'translateX(0)' : 'translateX(100%)',
                transition: 'transform 0.28s cubic-bezier(.4,0,.2,1)',
                display: 'flex', flexDirection: 'column',
            }}>
                {/* Header */}
                <div style={{ background: '#1a2440', padding: '16px 20px 14px', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <i className="fas fa-tasks" style={{ color: '#e67d21', fontSize: 18 }}></i>
                            <span style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>{t('todo.gorevlerim', 'Görevlerim')}</span>
                            {activeCount > 0 && (
                                <span style={{
                                    background: '#e67d21', color: '#fff', borderRadius: 20,
                                    fontSize: 11, fontWeight: 700, padding: '1px 8px', minWidth: 20, textAlign: 'center',
                                }}>{activeCount}</span>
                            )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {/* Takvim tarih seçici */}
                            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 4 }}>
                                <button
                                    title={isToday ? t('todo.tarihSec', 'Tarih seç') : `${t('todo.secili', 'Seçili')}: ${shortDate(selectedStr)} – ${t('todo.bugune', 'Bugüne dön')} ×`}
                                    onClick={() => (datePickerRef.current as any)?.showPicker?.() || datePickerRef.current?.click()}
                                    style={{
                                        background: isToday ? 'rgba(255,255,255,0.10)' : '#e67d21',
                                        border: 'none', borderRadius: 8,
                                        color: '#fff', fontSize: 13, cursor: 'pointer',
                                        padding: '5px 10px', display: 'flex', alignItems: 'center', gap: 5,
                                        fontWeight: isToday ? 400 : 700,
                                    }}
                                >
                                    <i className="fas fa-calendar-alt" style={{ fontSize: 13 }}></i>
                                    {!isToday && <span style={{ fontSize: 11 }}>{shortDate(selectedStr)}</span>}
                                </button>
                                {!isToday && (
                                    <button
                                        title={t('todo.bugune', 'Bugüne dön')}
                                        onClick={() => setSelectedDate(new Date(calToday))}
                                        style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.45)', fontSize: 14, cursor: 'pointer', padding: '2px 4px', lineHeight: 1 }}
                                    >×</button>
                                )}
                                <input
                                    ref={datePickerRef}
                                    type="date"
                                    value={selectedStr}
                                    onChange={e => {
                                        if (e.target.value) setSelectedDate(new Date(e.target.value + 'T00:00:00'));
                                    }}
                                    style={{ position: 'absolute', opacity: 0, width: 1, height: 1, top: 0, left: 0, pointerEvents: 'none' }}
                                />
                            </div>
                            <button
                                onClick={onClose}
                                style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 20, cursor: 'pointer', lineHeight: 1, padding: 4, borderRadius: 6 }}
                            >×</button>
                        </div>
                    </div>
                </div>

                {/* Yeni görev girişi */}
                <div style={{ padding: '12px 16px 10px', background: '#f8f9fc', borderBottom: '1px solid #e8ecf4', flexShrink: 0 }}>
                    {/* Renk seçimi (sadece todo modu) */}
                    {!newDateEnabled && (
                        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                            {PASTEL_COLORS.map(c => (
                                <button
                                    key={c.value}
                                    title={c.label}
                                    onClick={() => setNewColor(c.value)}
                                    style={{
                                        width: 22, height: 22, borderRadius: '50%', background: c.value,
                                        border: newColor === c.value ? '2.5px solid #1a2440' : '2px solid #d1d5db',
                                        cursor: 'pointer',
                                        transform: newColor === c.value ? 'scale(1.2)' : 'scale(1)',
                                        transition: 'transform 0.15s, border 0.15s', flexShrink: 0,
                                    }}
                                />
                            ))}
                        </div>
                    )}
                    {/* Metin + mikrofon + ekle */}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <div style={{ position: 'relative', flex: 1 }}>
                            <input
                                ref={inputRef}
                                value={newText}
                                onChange={e => setNewText(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder={newDateEnabled ? t('todo.takvimeEkle', 'Takvime ekle...') : t('todo.yeniGorevEkle', 'Yeni görev ekle...')}
                                style={{
                                    width: '100%', padding: '9px 38px 9px 12px',
                                    borderRadius: 10, border: '1.5px solid #d1d5db',
                                    fontSize: 13, outline: 'none', background: '#fff',
                                    boxSizing: 'border-box', transition: 'border 0.15s',
                                }}
                                onFocus={e => (e.target.style.borderColor = '#1a2440')}
                                onBlur={e => (e.target.style.borderColor = '#d1d5db')}
                            />
                            <button
                                title={listening ? t('todo.durdur', 'Durdur') : t('todo.sesliGiris', 'Sesli giriş')}
                                onClick={listening ? stopListening : startListening}
                                style={{
                                    position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    color: listening ? '#e67d21' : '#9ca3af', fontSize: 15, padding: 4, lineHeight: 1,
                                }}
                            >
                                <i className={`fas fa-microphone${listening ? '-slash' : ''}`} style={{ animation: listening ? 'pulse 1s infinite' : 'none' }}></i>
                            </button>
                        </div>
                        <button
                            onClick={handleAdd}
                            disabled={!newText.trim()}
                            style={{
                                background: newText.trim() ? (newDateEnabled ? '#e67d21' : '#1a2440') : '#c4c9d4',
                                color: '#fff', border: 'none', borderRadius: 10,
                                padding: '9px 14px', cursor: newText.trim() ? 'pointer' : 'default',
                                fontWeight: 700, fontSize: 18, lineHeight: 1,
                                transition: 'background 0.15s', flexShrink: 0,
                            }}
                        >+</button>
                    </div>
                    {/* Tarih seç satırı */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 12, color: '#6b7280', userSelect: 'none' }}>
                            <input
                                type="checkbox"
                                checked={newDateEnabled}
                                onChange={e => setNewDateEnabled(e.target.checked)}
                                style={{ width: 14, height: 14, cursor: 'pointer', accentColor: '#e67d21' }}
                            />
                            <i className="fas fa-calendar-alt" style={{ color: newDateEnabled ? '#e67d21' : '#9ca3af', fontSize: 11 }}></i>
                            {t('todo.tarihSec', 'Tarih seç')}
                        </label>
                        {newDateEnabled && (
                            <>
                                <input
                                    type="date"
                                    value={newDateStr}
                                    onChange={e => setNewDateStr(e.target.value)}
                                    style={{
                                        border: '1.5px solid #e67d21', borderRadius: 7, padding: '3px 8px',
                                        fontSize: 12, outline: 'none', color: '#1a2440', background: '#fff', cursor: 'pointer',
                                    }}
                                />
                                <span style={{ fontSize: 11, color: '#e67d21', fontWeight: 600 }}>
                                    <i className="fas fa-calendar-check" style={{ marginRight: 3 }}></i>{t('todo.takvimeEklenecek', 'Takvime eklenecek')}
                                </span>
                            </>
                        )}
                    </div>
                </div>

                {/* Filtre */}
                <div style={{ display: 'flex', gap: 0, padding: '10px 16px 0', flexShrink: 0 }}>
                    {(['all', 'active', 'done'] as const).map(f => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            style={{
                                flex: 1, padding: '6px 0', border: 'none',
                                background: filter === f ? '#1a2440' : 'transparent',
                                color: filter === f ? '#fff' : '#6b7280',
                                borderRadius: f === 'all' ? '8px 0 0 8px' : f === 'done' ? '0 8px 8px 0' : '0',
                                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                transition: 'background 0.15s',
                            }}
                        >
                            {f === 'all' ? t('label.tumu', 'Tümü') : f === 'active' ? t('todo.aktif', 'Aktif') : t('todo.tamamlanan', 'Tamamlanan')}
                        </button>
                    ))}
                </div>

                {/* Birleşik liste */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '10px 16px 16px' }}>
                    {/* Yükleniyor */}
                    {(loading || calLoading) && (
                        <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>
                            <i className="fas fa-spinner fa-spin" style={{ fontSize: 24 }}></i>
                        </div>
                    )}

                    {/* Boş durum */}
                    {!loading && !calLoading && filtered.length === 0 && visibleCalNotes.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>
                            <i className="fas fa-clipboard-list" style={{ fontSize: 32, marginBottom: 10, display: 'block' }}></i>
                            <span style={{ fontSize: 13 }}>{t('todo.gorevYok', 'Görev yok')}</span>
                        </div>
                    )}

                    {/* Todo kartları */}
                    {filtered.map((todo, index) => (
                        <div
                            key={todo.todoID}
                            draggable
                            onDragStart={() => handleDragStart(index)}
                            onDragOver={e => handleDragOver(e, index)}
                            onDrop={() => handleDrop(index)}
                            onDragEnd={handleDragEnd}
                            style={{
                                transition: 'transform 0.15s, opacity 0.15s',
                                opacity: dragIndexRef.current === index ? 0.4 : 1,
                                transform: dragOverIndex === index && dragIndexRef.current !== index
                                    ? (dragIndexRef.current !== null && dragIndexRef.current < index ? 'translateY(4px)' : 'translateY(-4px)')
                                    : 'none',
                            }}
                        >
                            <TodoCard
                                todo={todo}
                                onToggle={() => handleToggle(todo)}
                                onDelete={() => handleDelete(todo.todoID)}
                                onUpdate={(baslik, renk) => {
                                    axiosInstance.put(`/api/TodoGorevler/${todo.todoID}`, {
                                        kullaniciID, baslik, renk, tamamlandi: todo.tamamlandi, siraNo: todo.siraNo,
                                    }).then((r: { data: TodoItem }) => setTodos(prev => prev.map(item => item.todoID === todo.todoID ? r.data : t)));
                                }}
                            />
                        </div>
                    ))}

                    {/* Takvim tarihi başlığı + tarih seçici */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '12px 0 4px' }}>
                        <div style={{ flex: 1, height: 1, background: '#e8ecf4' }} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, position: 'relative' }}>
                            <i className="fas fa-calendar-alt" style={{ color: '#e67d21', fontSize: 11 }}></i>
                            <button
                                onClick={() => (datePickerRef.current as any)?.showPicker?.() || datePickerRef.current?.click()}
                                style={{
                                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                    fontSize: 11, fontWeight: 700, color: '#e67d21', whiteSpace: 'nowrap',
                                    textDecoration: 'underline dotted',
                                }}
                                title={t('todo.tarihDegistir', 'Tarih değiştir')}
                            >
                                {formatDate(selectedDate).toUpperCase()}
                            </button>
                            {!isToday && (
                                <button
                                    onClick={() => setSelectedDate(new Date(calToday))}
                                    title={t('todo.bugune', 'Bugüne dön')}
                                    style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 12, padding: '0 2px', lineHeight: 1 }}
                                >×</button>
                            )}
                        </div>
                        <div style={{ flex: 1, height: 1, background: '#e8ecf4' }} />
                    </div>

                    {/* Takvim notları */}
                    {visibleCalNotes.map(note => (
                        <div key={note.notID} style={{ marginBottom: 8 }}>
                            {editingNoteId === note.notID ? (
                                <div style={{ background: '#f0f3fa', borderRadius: 12, padding: 12, border: '1.5px solid #c7d9f8' }}>
                                    <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                                        {CAL_COLOR_OPTIONS.map(c => (
                                            <button
                                                key={c.value}
                                                title={c.label}
                                                onClick={() => setCalFormRenk(c.value)}
                                                style={{
                                                    width: 20, height: 20, borderRadius: '50%', background: c.bg, padding: 0, cursor: 'pointer',
                                                    border: calFormRenk === c.value ? '2.5px solid #1a2440' : '1.5px solid #d1d5db',
                                                    transform: calFormRenk === c.value ? 'scale(1.2)' : 'scale(1)',
                                                    transition: 'transform 0.12s',
                                                }}
                                            />
                                        ))}
                                    </div>
                                    <input
                                        value={calFormBaslik}
                                        onChange={e => setCalFormBaslik(e.target.value)}
                                        placeholder={t('label.baslik')}
                                        autoFocus
                                        onKeyDown={e => { if (e.key === 'Enter') handleCalUpdate(note.notID, note.notTarihi); if (e.key === 'Escape') cancelEdit(); }}
                                        style={{ width: '100%', padding: '6px 10px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 13, outline: 'none', background: '#fff', boxSizing: 'border-box', marginBottom: 6 }}
                                    />
                                    <textarea
                                        value={calFormIcerik}
                                        onChange={e => setCalFormIcerik(e.target.value)}
                                        placeholder={t('label.ara')}
                                        rows={2}
                                        style={{ width: '100%', padding: '6px 10px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 12, outline: 'none', resize: 'none', boxSizing: 'border-box', background: '#fff' }}
                                    />
                                    <div style={{ display: 'flex', gap: 6, marginTop: 8, justifyContent: 'flex-end' }}>
                                        <button onClick={cancelEdit} style={{ background: 'none', border: '1.5px solid #d1d5db', borderRadius: 8, padding: '5px 12px', fontSize: 12, cursor: 'pointer', color: '#6b7280' }}>{t('btn.iptal')}</button>
                                        <button
                                            onClick={() => handleCalUpdate(note.notID, note.notTarihi)}
                                            disabled={!calFormBaslik.trim()}
                                            style={{ background: calFormBaslik.trim() ? '#1a2440' : '#c4c9d4', color: '#fff', border: 'none', borderRadius: 8, padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: calFormBaslik.trim() ? 'pointer' : 'default' }}
                                        >{t('btn.guncelle')}</button>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ background: calColorToBg(note.notRengi), borderRadius: 12, padding: '10px 12px', boxShadow: '0 1px 4px rgba(26,36,64,0.06)' }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontWeight: 600, fontSize: 13, color: '#1a2440', lineHeight: 1.3, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                                                <span>{note.notBaslik}</span>
                                                <span style={{
                                                    fontSize: 10, color: '#e67d21', fontWeight: 700,
                                                    background: 'rgba(230,125,33,0.12)', borderRadius: 5, padding: '1px 6px',
                                                }}>
                                                    <i className="fas fa-calendar-day" style={{ marginRight: 3 }}></i>
                                                    {shortDate(note.notTarihi)}
                                                </span>
                                            </div>
                                            {note.notIcerik && (
                                                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3, lineHeight: 1.4 }}>{note.notIcerik}</div>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                                            <button onClick={() => startEditNote(note)} title={t('btn.duzenle')}
                                                style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 12, padding: '2px 4px', borderRadius: 4 }}
                                                onMouseEnter={e => (e.currentTarget.style.color = '#1a2440')}
                                                onMouseLeave={e => (e.currentTarget.style.color = '#9ca3af')}
                                            ><i className="fas fa-pen"></i></button>
                                            <button onClick={() => handleCalDelete(note.notID)} title={t('btn.sil')}
                                                style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 12, padding: '2px 4px', borderRadius: 4 }}
                                                onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')}
                                                onMouseLeave={e => (e.currentTarget.style.color = '#9ca3af')}
                                            ><i className="fas fa-trash"></i></button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Footer */}
                {todos.length > 0 && (
                    <div style={{
                        borderTop: '1px solid #e8ecf4', padding: '10px 16px',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        fontSize: 12, color: '#9ca3af', flexShrink: 0, background: '#fafbfd',
                    }}>
                        <span>{t('todo.gorevKaldi', '{{count}} görev kaldı', { count: activeCount })}</span>
                        <button
                            onClick={async () => {
                                const doneTodos = todos.filter(item => item.tamamlandi);
                                await Promise.all(doneTodos.map(item => axiosInstance.delete(`/api/TodoGorevler/${item.todoID}`)));
                                setTodos(prev => prev.filter(item => !item.tamamlandi));
                            }}
                            style={{ background: 'none', border: 'none', color: '#e67d21', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                        >
                            {t('todo.tamamlananlariTemizle', 'Tamamlananları temizle')}
                        </button>
                    </div>
                )}
            </div>

            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.4; }
                }
            `}</style>
        </>
    );
};

/* ── Görev Kartı ── */
interface CardProps {
    todo: TodoItem;
    onToggle: () => void;
    onDelete: () => void;
    onUpdate: (baslik: string, renk: string) => void;
}
const TodoCard: React.FC<CardProps> = ({ todo, onToggle, onDelete, onUpdate }) => {
    const { t } = useTranslation();
    const [editing, setEditing] = useState(false);
    const [editText, setEditText] = useState(todo.baslik);
    const [editColor, setEditColor] = useState(todo.renk);

    const saveEdit = () => {
        if (editText.trim() && (editText !== todo.baslik || editColor !== todo.renk)) {
            onUpdate(editText.trim(), editColor);
        }
        setEditing(false);
    };

    return (
        <div style={{
            background: todo.renk || '#f8f9fc',
            borderRadius: 12,
            padding: '10px 12px',
            marginBottom: 8,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            opacity: todo.tamamlandi ? 0.6 : 1,
            transition: 'opacity 0.2s',
            boxShadow: '0 1px 4px rgba(26,36,64,0.06)',
            cursor: 'grab',
        }}>
            {/* Sürükle ikon */}
            <span style={{ color: '#c4c9d4', fontSize: 13, marginTop: 3, flexShrink: 0, cursor: 'grab' }} title="Sürükle">
                <i className="fas fa-grip-vertical"></i>
            </span>
            {/* Checkbox */}
            <button
                onClick={onToggle}
                style={{
                    width: 22, height: 22, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                    border: `2px solid ${todo.tamamlandi ? '#10b981' : '#d1d5db'}`,
                    background: todo.tamamlandi ? '#10b981' : 'transparent',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.15s',
                }}
            >
                {todo.tamamlandi && <i className="fas fa-check" style={{ color: '#fff', fontSize: 10 }}></i>}
            </button>

            {/* İçerik */}
            <div style={{ flex: 1, minWidth: 0 }}>
                {editing ? (
                    <div>
                        {/* Renk seçimi (düzenleme) */}
                        <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                            {PASTEL_COLORS.map(c => (
                                <button
                                    key={c.value}
                                    onClick={() => setEditColor(c.value)}
                                    style={{
                                        width: 18, height: 18, borderRadius: '50%', background: c.value,
                                        border: editColor === c.value ? '2px solid #1a2440' : '1.5px solid #d1d5db',
                                        cursor: 'pointer', padding: 0,
                                        transform: editColor === c.value ? 'scale(1.2)' : 'scale(1)',
                                    }}
                                />
                            ))}
                        </div>
                        <input
                            value={editText}
                            onChange={e => setEditText(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditing(false); }}
                            onBlur={saveEdit}
                            autoFocus
                            style={{
                                width: '100%', border: '1.5px solid #1a2440', borderRadius: 6,
                                padding: '4px 8px', fontSize: 13, background: 'rgba(255,255,255,0.7)',
                                outline: 'none', boxSizing: 'border-box',
                            }}
                        />
                    </div>
                ) : (
                    <span
                        onDoubleClick={() => { setEditing(true); setEditText(todo.baslik); setEditColor(todo.renk); }}
                        style={{
                            fontSize: 13, color: '#1a2440', lineHeight: 1.4,
                            textDecoration: todo.tamamlandi ? 'line-through' : 'none',
                            cursor: 'text', wordBreak: 'break-word',
                        }}
                        title={t('btn.duzenle')}
                    >
                        {todo.baslik}
                    </span>
                )}
            </div>

            {/* Silme */}
            <button
                onClick={onDelete}
                style={{
                    background: 'none', border: 'none', color: '#9ca3af',
                    cursor: 'pointer', fontSize: 13, padding: '2px 4px',
                    flexShrink: 0, borderRadius: 4,
                    transition: 'color 0.15s',
                }}
                onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')}
                onMouseLeave={e => (e.currentTarget.style.color = '#9ca3af')}
                title={t('btn.sil')}
            >
                <i className="fas fa-trash"></i>
            </button>
        </div>
    );
};

export default TodoPanel;
